// @ts-nocheck
import { Fragment, useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import Flatpickr from 'react-flatpickr'
import { Calendar, FileX } from 'lucide-react'
import { CubeLoader } from '@/components/ui/cube-loader'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import HeaderTable from '@/components/reports/HeaderTable'
import { GET_EXTRACTION_HEADERS } from '@/graphql/publisher-dashboard-queries'
import 'flatpickr/dist/flatpickr.css'

const DATE_OPTIONS = { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'Australia/Sydney' }

/** A history date "DD/MM/YYYY" as the picker's "YYYY-MM-DD". */
const toPickerDate = (date) => date.split('/').reverse().join('-')

/** A picker date "YYYY-MM-DD" as a history date "DD/MM/YYYY". */
const fromPickerDate = (date) => date.split('-').reverse().join('/')

/**
 * A stored copy history as a dated list. A legacy list without dates is shown under the last run's date; invalid or
 * empty text gives an empty history.
 */
const historyOf = (json, fallbackDate) => {
    if (!json) return []
    try {
        const parsed = JSON.parse(json)
        if (!Array.isArray(parsed) || parsed.length === 0) return []
        if (parsed[parsed.length - 1]?.array) return parsed.filter((entry) => entry?.date)
        return fallbackDate ? [{ date: fallbackDate, array: parsed }] : []
    } catch {
        return []
    }
}

/** The items of a history on one date, without blanks. */
const entriesFor = (history, date) => history.find((entry) => entry.date === date)?.array?.filter(Boolean) ?? []

/** One tab's copy, or why there is none: a field such as descriptions may have been written later than the others. */
const CopyTab = ({ entries, label }) =>
    entries.length > 0 ? (
        <HeaderTable headers={entries} />
    ) : (
        <p className="small text-blue-300 px-4 py-3 mb-0">No {label} were written on this date.</p>
    )

const ExtractionReport = () => {
    const { id } = useParams()
    const campaign = useQuery(GET_EXTRACTION_HEADERS, {
        variables: id ? { where: { id } } : {},
    })
    const fp = useRef(null)
    const [selectedDate, setSelectedDate] = useState(null)
    const [tmDates, setTmDates] = useState([])

    const data = campaign.data?.campaigns?.[0]
    const lastRunDate = data?.lastExtractedAt
        ? new Date(data.lastExtractedAt).toLocaleDateString('en-AU', DATE_OPTIONS)
        : null
    const histories = {
        headline: historyOf(data?.headers, lastRunDate),
        primary: historyOf(data?.subheaders, lastRunDate),
        description: historyOf(data?.descriptions, lastRunDate),
    }

    const date = () => {
        const value = selectedDate ?? lastRunDate
        if (!value) return null
        const [day, month, year] = value.split('/')
        return new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }).format(
            new Date(`${year}-${month}-${day}`),
        )
    }

    const handleDateChange = (_selectedDates, dateStr) => {
        setSelectedDate(fromPickerDate(dateStr))
    }

    useEffect(() => {
        if (tmDates.length === 0) {
            const dates = [
                ...new Set(Object.values(histories).flatMap((history) => history.map((entry) => entry.date))),
            ]
                .map(toPickerDate)
                .sort()
            if (dates.length > 0) {
                setSelectedDate(fromPickerDate(dates[dates.length - 1]))
                setTmDates(dates)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaign])

    return (
        <div className="card card-block">
            {campaign.loading ? (
                <CubeLoader size="sm" />
            ) : (
                <Fragment>
                    <div className="card-header p-4 flex-column">
                        <div className="d-flex justify-content-between align-items-center">
                            <h6 className="card-title">Time Machine</h6>
                            {date() && (
                                <div className="d-flex align-items-center gap-1">
                                    <button
                                        type="button"
                                        className="border-0 bg-transparent p-0 d-flex align-items-center text-blue-300"
                                        onClick={() => fp.current?.flatpickr?.open()}
                                        aria-label="Open date picker"
                                    >
                                        <Calendar size={20} />
                                    </button>
                                    <Flatpickr
                                        ref={fp}
                                        value={selectedDate ? toPickerDate(selectedDate) : undefined}
                                        options={{ enable: tmDates }}
                                        className="hidden-input"
                                        onChange={handleDateChange}
                                    />
                                    <span className="text-blue-300">{date()}</span>
                                </div>
                            )}
                        </div>
                        <span className="small text-white">Generative AI Ad Copy</span>
                    </div>
                    <div className="card-body card-thumbnail p-0">
                        {date() ? (
                            <Tabs defaultValue="headline">
                                <div className="tab-bottom-bordered">
                                    <TabsList className="tw:bg-transparent tw:border-b tw:border-border tw:rounded-none tw:justify-start tw:mx-4 tw:mb-0">
                                        <TabsTrigger value="headline" className="tw:text-white">
                                            Headline
                                        </TabsTrigger>
                                        <TabsTrigger value="primary" className="tw:text-white">
                                            Primary Text
                                        </TabsTrigger>
                                        <TabsTrigger value="description" className="tw:text-white">
                                            Description
                                        </TabsTrigger>
                                    </TabsList>
                                </div>
                                <TabsContent value="headline">
                                    <CopyTab entries={entriesFor(histories.headline, selectedDate)} label="headlines" />
                                </TabsContent>
                                <TabsContent value="primary">
                                    <CopyTab
                                        entries={entriesFor(histories.primary, selectedDate)}
                                        label="primary texts"
                                    />
                                </TabsContent>
                                <TabsContent value="description">
                                    <CopyTab
                                        entries={entriesFor(histories.description, selectedDate)}
                                        label="descriptions"
                                    />
                                </TabsContent>
                            </Tabs>
                        ) : (
                            <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:py-12 tw:text-gray-400">
                                <FileX size={40} className="tw:mb-3 tw:opacity-30" />
                                <p className="tw:text-sm">No ad copy available yet.</p>
                            </div>
                        )}
                    </div>
                </Fragment>
            )}
        </div>
    )
}

export default ExtractionReport
